/**
 * Invite member — the detail panel's "invite" mode.
 *
 * Role selection, per-chip "view what this role grants", and "+ Create new
 * role" all live in <select-role>, reused unchanged here. DUMB atomic
 * SchmancyElement: all presentation is the `inviteView` computed; the view
 * reads it and writes intents (inviteDraft / detailState / members.state's
 * membersAction$ channel — members.sync.ts, web-side, performs the write,
 * via the createInvite callable). Derives nothing in render().
 *
 * On successful send, the panel closes and the new pending row appears
 * inline in the member list the moment the invite-created trigger's mirror
 * write reaches the live members listener — the same live row
 * invite-detail.view.ts reads by token when an admin clicks into it.
 */

import { awaitByRequestId$ } from "@mhmo91/rx";
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, type TemplateResult } from "lit";
import { state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { firstValueFrom, takeUntil } from "rxjs";
import { activeActor } from "../../auth/actor.state.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";
import {
	detailState,
	eventOptions,
	inviteDraft,
	inviteView,
	membersAction$,
	membersResult$,
} from "../members.state.ts";
import "../roles/select-role.view.ts";

@schmancyElement("invite-member")
export class InviteMember extends LocalizeMixin(SchmancyElement) {
	// Round-trip feedback for the createInvite callable — blocks a second click
	// firing a duplicate invite and drives the button's spinner/label.
	@state() private sending = false;
	@state() private failed = false;

	private async send(): Promise<void> {
		const { draft, canSend } = inviteView.get();
		if (!canSend || this.sending) return;
		this.sending = true;
		try {
			const requestId = crypto.randomUUID();
			const result = firstValueFrom(
				awaitByRequestId$(membersResult$, requestId).pipe(
					takeUntil(this.disconnecting),
				),
			);
			membersAction$.next({
				kind: "invite",
				requestId,
				draft: structuredClone(draft),
			});
			if (!(await result).ok) {
				this.failed = true;
				return;
			}
			detailState.replace({ kind: "none" });
		} catch {
			this.failed = true;
		} finally {
			this.sending = false;
		}
	}

	override render(): TemplateResult {
		const { draft, isSpecific, selectedEventIds, canSend } = inviteView.get();

		return html`
			<div class="flex flex-col h-full">
				<div class="flex items-center justify-end px-5 pt-5 pb-3">
					<schmancy-icon-button
						icon="close"
						?disabled=${this.sending}
						variant="text"
						aria-label=${this.t("admin.members.invite.closeAriaLabel")}
						@click=${() => detailState.replace({ kind: "none" })}
					></schmancy-icon-button>
				</div>

				<div ?inert=${this.sending} ${overflowWithin()} class="px-5 py-2 flex-1 min-h-0 flex flex-col gap-4">
					<schmancy-input
						label=${this.t("admin.members.common.nameLabel")}
						.value=${draft.displayName}
						@input=${(e: CustomEvent<{ value: string }>) => inviteDraft.set({ displayName: e.detail.value })}
					></schmancy-input>

					<schmancy-input
						label=${this.t("admin.members.invite.emailLabel")}
						type="email"
						placeholder=${this.t("admin.members.invite.emailPlaceholder")}
						.value=${draft.email}
						@input=${(e: CustomEvent<{ value: string }>) => inviteDraft.set({ email: e.detail.value })}
					></schmancy-input>

					<div class="flex flex-col gap-2">
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.members.common.roleLabel")}</schmancy-typography>
						<select-role
							.value=${draft.roleId}
							@change=${(e: CustomEvent<{ value: string }>) => {
								if (e.detail.value === draft.roleId) return;
								inviteDraft.set({ roleId: e.detail.value });
							}}
						></select-role>
					</div>

					<div class="flex flex-col gap-2">
						<div class="flex items-center gap-1.5">
							<schmancy-icon class="text-surface-onVariant text-base" aria-hidden="true">event</schmancy-icon>
							<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.members.common.eventAccessLabel")}</schmancy-typography>
						</div>
						<schmancy-chips
							required
							wrap
							.value=${draft.scope.kind}
							@change=${(
								e: CustomEvent<{ value: "all-events" | "events" }>,
							) => {
								const kind = e.detail.value;
								if (kind === draft.scope.kind) return;
								inviteDraft.set({
									scope:
										kind === "all-events"
											? {
													kind: "all-events",
													orgId: activeActor.get()?.orgId ?? "",
												}
											: { kind: "events", eventIds: [] },
								});
							}}
						>
							<schmancy-filter-chip value="all-events">${this.t("admin.members.invite.allEventsChip")}</schmancy-filter-chip>
							<schmancy-filter-chip value="events">${this.t("admin.members.invite.specificEventsChip")}</schmancy-filter-chip>
						</schmancy-chips>

						${when(
							isSpecific,
							() => html`
								<schmancy-chips
									multi
									wrap
									class="w-full"
									.value=${selectedEventIds}
									@change=${(e: CustomEvent<{ value: string[] }>) => {
										inviteDraft.set({
											scope: { kind: "events", eventIds: e.detail.value },
										});
									}}
								>
									${repeat(
										eventOptions.value,
										(ev) => ev.id,
										(ev) =>
											html`<schmancy-filter-chip .value=${ev.id}>${ev.name}</schmancy-filter-chip>`,
									)}
								</schmancy-chips>
								${when(
									selectedEventIds.length === 0,
									() => html`
										<schmancy-typography type="body" token="xs" class="text-surface-onVariant"> ${this.t("admin.members.invite.selectAtLeastOne")} </schmancy-typography>
									`,
								)}
							`,
						)}

						${when(
							!isSpecific,
							() => html`
								<schmancy-typography type="body" token="xs" class="text-surface-onVariant">
									${this.t("admin.members.invite.coversAllEvents")}
								</schmancy-typography>
							`,
						)}
					</div>
				</div>

				<schmancy-surface opacity="translucent" elevated class="sticky bottom-0 px-5 py-4 pt-2 flex justify-end gap-2">
					<schmancy-button variant="outlined" ?disabled=${this.sending} @click=${() => detailState.replace({ kind: "none" })}>${this.t("admin.common.cancel")}</schmancy-button>
					<schmancy-button variant="filled" .busy=${this.sending} color=${this.failed ? "error" : "primary"} ?disabled=${!canSend} @click=${() => this.send()}>
												<span style="display:grid"><span style=${this.failed ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failed ? "true" : "false"}>${this.t("admin.members.invite.send")}</span><span style=${this.failed ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failed ? "false" : "true"}>${this.t("admin.common.retrySend")}</span></span>
					</schmancy-button>
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"invite-member": InviteMember;
	}
}
